import { useEffect } from 'react';
import { IntakeProvider, useIntake } from './state/IntakeContext';
import { WelcomeScreen } from './components/WelcomeScreen';
import { UploadScreen } from './components/UploadScreen';
import { ChatScreen } from './components/ChatScreen';
import { ReviewScreen } from './components/ReviewScreen';
import { SubmittedScreen } from './components/SubmittedScreen';
import { Storyboard } from './components/Storyboard';
import { tForLang } from './i18n';
import { DEFAULT_LANGUAGE } from './i18n';
import { logEvent } from './lib/telemetry';
import './App.css';

/** Detect the `?storyboard=1` query-string flag so devs can open the
 *  component catalog without going through the intake flow. Read at
 *  module scope so it's resolved once per load; no reactivity needed. */
function isStoryboardMode(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('storyboard') === '1';
}

/** Root app — wraps everything in IntakeProvider (the Storyboard's
 *  ThemeToggle transitively needs useTranslation, which reads the language
 *  from context). The storyboard is a sibling of the phase router; not
 *  both at once. */
export function App() {
  return (
    <IntakeProvider>
      {isStoryboardMode() ? <Storyboard /> : <PhaseRouter />}
    </IntakeProvider>
  );
}

function PhaseRouter() {
  const { phase, state, sessionId } = useIntake();
  const lang = state.language ?? DEFAULT_LANGUAGE;

  useEffect(() => {
    logEvent('page_view', {
      route: phase,
      session_id: sessionId,
      language: lang,
    });
  }, [phase, sessionId, lang]);

  // Skip link is always present for keyboard users, outside the phase switch.
  const skipLink = (
    <a href="#main" className="cf-skip-link">
      {tForLang(lang, 'a11y.skip_to_main')}
    </a>
  );

  switch (phase) {
    case 'welcome':
      return (
        <>
          {skipLink}
          <WelcomeScreen />
        </>
      );
    case 'upload':
      return (
        <>
          {skipLink}
          <UploadScreen />
        </>
      );
    case 'chat':
      return (
        <>
          {skipLink}
          <ChatScreen />
        </>
      );
    case 'review':
      return (
        <>
          {skipLink}
          <ReviewScreen />
        </>
      );
    case 'submitted':
      return (
        <>
          {skipLink}
          <SubmittedScreen />
        </>
      );
  }
}
